import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { scroller } from "react-scroll";

export default function Footer() {
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingScroll, setPendingScroll] = useState<string | null>(null);

  const smoothScroll = (section: string) => {
    scroller.scrollTo(section, {
      duration: 1000,
      delay: 0,
      smooth: "easeInOutQuart",
      offset: -80,
    });
  };

  // Cross-page route layout synchronization listener
  useEffect(() => {
    if (pendingScroll) {
      const timer = setTimeout(() => {
        smoothScroll(pendingScroll);
        setPendingScroll(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, pendingScroll]);

  const handleLinkClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    page: string | null,
    section: string,
  ) => {
    e.preventDefault();
    const targetPage = page || "/";

    if (section === "home") {
      if (location.pathname !== "/") {
        navigate("/");
      }
      window.scrollTo(0, 0);
      return;
    }

    if (location.pathname === targetPage) {
      smoothScroll(section);
    } else {
      setPendingScroll(section);
      navigate(targetPage);
    }
  };

  return (
    <footer className="font-sans" style={{ backgroundColor: "#0F1C35" }}>
      {/* Main Footer Content */}
      <div className="w-full px-6 md:px-10 lg:px-16 xl:px-24 max-w-[1400px] mx-auto py-20 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8">
          {/* Brand Presentation Column - Takes 3 Cols */}
          <div className="lg:col-span-3 lg:pr-6">
            <div className="mb-6">
              <img
                src="https://i.postimg.cc/B6RH8Bcz/1000016498.jpg"
                alt="BICCI Logo"
                className="h-14 w-auto object-contain"
              />
            </div>
            <p className="text-sm text-white/70 leading-relaxed mb-8 max-w-sm">
              A premier bilateral platform connecting entrepreneurs, investors,
              and institutions across two of the world's most dynamic economies.
            </p>
          </div>

          {/* Sequential Navbar Categories Container - Takes 9 Cols */}
          <div className="lg:col-span-9 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-10 md:gap-6 lg:gap-8">
            {/* 1. Home & 2. About Columns */}
            <div>
              <a
                href="#home"
                onClick={(e) => handleLinkClick(e, "/", "home")}
                className="block text-xs font-bold tracking-[0.15em] uppercase text-white mb-6 hover:text-[#0077b6] transition-colors"
              >
                Home
              </a>
              <h4 className="text-xs font-bold tracking-[0.15em] uppercase text-white/40 mb-4 cursor-default">
                About BICCI
              </h4>
              <ul className="space-y-3">
                {[
                  { label: "Who We Are", section: "who-we-are", page: "/" },
                  {
                    label: "Mission & Vision",
                    section: "mission-vision",
                    page: "/about",
                  },
                  {
                    label: "Our Core Values",
                    section: "core-values",
                    page: "/about",
                  },
                  // {
                  //   label: "Ambassador's Message",
                  //   section: "ambassador-message",
                  //   page: "/about",
                  // },
                  // {
                  //   label: "President's Message",
                  //   section: "president-message",
                  //   page: "/about",
                  // },
                  {
                    label: "Board Members",
                    section: "board-members",
                    page: "/about",
                  },
                ].map((item) => (
                  <li key={item.label}>
                    <a
                      href={`#${item.section}`}
                      onClick={(e) =>
                        handleLinkClick(e, item.page, item.section)
                      }
                      className="group flex items-center text-sm text-white/70 hover:text-white transition-colors cursor-pointer"
                    >
                      <span className="w-0 h-[1px] bg-[#0077b6] mr-0 transition-all duration-300 group-hover:w-3 group-hover:mr-2 flex-shrink-0" />
                      <span className="truncate">{item.label}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* 3. Services, 4. Trade, 5. Members, 6. Events Columns */}
            <div>
              <h4 className="block text-xs font-bold tracking-[0.15em] uppercase text-white mb-6 hover:text-[#0077b6] transition-colors">
                Core Operations
              </h4>
              <ul className="space-y-4">
                {[
                  { label: "Services", section: "services", page: "/" },
                  { label: "Trade & Invest", section: "trade", page: "/" },
                  { label: "Members", section: "members", page: "/" },
                  { label: "Events", section: "events", page: "/" },
                ].map((item) => (
                  <li key={item.label}>
                    <a
                      href={`#${item.section}`}
                      onClick={(e) =>
                        handleLinkClick(e, item.page, item.section)
                      }
                      className="group flex items-center text-base font-medium text-white/90 hover:text-[#0077b6] transition-colors cursor-pointer"
                    >
                      <span className="w-0 h-[1px] bg-[#0077b6] mr-0 transition-all duration-300 group-hover:w-3 group-hover:mr-2 flex-shrink-0" />
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* 7. News & Insights Column */}
            <div>
              <h4 className="text-xs font-bold tracking-[0.15em] uppercase text-white mb-6 cursor-default">
                News & Insights
              </h4>
              <ul className="space-y-3">
                {[
                  {
                    label: "Annual Reports",
                    section: "annual-reports",
                    page: "/news/annual-reports",
                  },
                  {
                    label: "Market Insights",
                    section: "market-insights",
                    page: "/news/market-insights",
                  },
                  {
                    label: "Policy Papers",
                    section: "policy-papers",
                    page: "/news/policy-papers",
                  },
                  {
                    label: "Press Releases",
                    section: "press-release",
                    page: "/news/press-release",
                  },
                  {
                    label: "Bilateral Trade News",
                    section: "bilateral-trade-news",
                    page: "/news/bilateral-trade-news",
                  },
                  {
                    label: "Business Articles",
                    section: "business-articles",
                    page: "/news/business-articles",
                  },
                  {
                    label: "Chamber Updates",
                    section: "chamber-updates",
                    page: "/news/chamber-updates",
                  },
                ].map((item) => (
                  <li key={item.label}>
                    <a
                      href={`#${item.section}`}
                      onClick={(e) =>
                        handleLinkClick(e, item.page, item.section)
                      }
                      className="group flex items-center text-sm text-white/70 hover:text-white transition-colors cursor-pointer"
                    >
                      <span className="w-0 h-[1px] bg-[#0077b6] mr-0 transition-all duration-300 group-hover:w-3 group-hover:mr-2 flex-shrink-0" />
                      <span className="truncate">{item.label}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* 8. Activity, 9. Gallery, 10. Contact Columns */}
            <div>
              <h4 className="block text-xs font-bold tracking-[0.15em] uppercase text-white mb-6 hover:text-[#0077b6] transition-colors">
                Gallery & Connect
              </h4>
              <ul className="space-y-5 mb-6">
                {[
                  { label: "Activity", section: "activity", page: "/" },
                  { label: "Gallery", section: "gallery", page: "/" },
                  { label: "Contact Us", section: "contact", page: "/" },
                ].map((item) => (
                  <li key={item.label}>
                    <a
                      href={`#${item.section}`}
                      onClick={(e) =>
                        handleLinkClick(e, item.page, item.section)
                      }
                      className="group flex items-center text-base font-medium text-white/90 hover:text-[#0077b6] transition-colors cursor-pointer"
                    >
                      <span className="w-0 h-[1px] bg-[#0077b6] mr-0 transition-all duration-300 group-hover:w-3 group-hover:mr-2 flex-shrink-0" />
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>

              {/* Explicit Contact Meta Links */}
              <div className="pt-4 border-t border-white/10 space-y-3">
                <a
                  href="mailto:dhaka@bicci.org"
                  className="flex items-center gap-2.5 text-xs text-white/60 hover:text-white transition-colors"
                >
                  <svg
                    className="w-4 h-4 text-[#0077b6]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                    />
                  </svg>
                  info@bicci-it.com
                </a>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=Dhaka,+Bangladesh"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 text-xs text-white/60 hover:text-white transition-colors"
                >
                  <svg
                    className="w-4 h-4 text-[#0077b6]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
                    />
                  </svg>
                  Dhaka, Bangladesh
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Legal Bar */}
      <div className="border-t border-white/10 bg-black/20">
        <div className="w-full px-6 md:px-10 lg:px-16 xl:px-24 max-w-[1400px] mx-auto py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[15px] text-white/50 text-center md:text-left">
            &copy; {new Date().getFullYear()} Bangladesh Italy Chamber of
            Commerce and Industry. All rights reserved.
          </p>
          <a
            href="https://ventureit.tech"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-1.5 text-[15px] font-medium text-white/50 hover:text-white transition-colors cursor-pointer"
          >
            Developed By{" "}
            <span
              style={{ color: "#0077b6" }}
              className="group-hover:text-white transition-colors"
            >
              Venture IT
            </span>
          </a>
        </div>
      </div>
    </footer>
  );
}